import { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import { Alert, Box, Button, Checkbox, Divider, FormControlLabel, Grid, Radio, Switch, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { User, newUser, Email, Phone, Address, newEmail, newPhone, newAddress } from "..";
import { ErpSolution, Paymentprofile, PrepaymentSolution, newPaymentprofile } from "../PaymentProfile";
import { ClickableIcon } from "../../components/ui/Icon";
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import CustomPhoneInput from "../../components/ui/CustomPhoneInput";
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input'
import { SingleSelectChip, SingleSelectSearch } from "../../components/ui/DropdownSelect";
import { SingleDatePicker } from "../../components/ui/SingleDatePicker";

// interface UserFormProps {
//   user: User;
//   error?: string;
//   autocomplete?: boolean; // toggle autocomplete, nice for managers
// }

interface PaymentProfileFormProps {
    ErpSolutionList?: ErpSolution[];
    PrepaymentSolutionList?: PrepaymentSolution[];
    paymentprofile: Paymentprofile;
    error?: string;
    autocomplete?: boolean; // toggle autocomplete, nice for managers
  }

export interface PaymentProfileFormRef {
  valid: () => boolean;
  getPaymentprofile: () => Paymentprofile;
}

// export interface UserFormRef {
//     valid: () => boolean;
//     getUser: () => User;
//   }

export const PaymentProfileForm = forwardRef((props: PaymentProfileFormProps, ref) => {
  const { error, autocomplete = true ,ErpSolutionList, PrepaymentSolutionList} = props;
  const [paymentprofile, setPaymentprofile] = useState<Paymentprofile>(newPaymentprofile());
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setPaymentprofile(props.paymentprofile);
  }, [props.paymentprofile]);

  const [touched, setTouched] = useState<{
    base: Record<string, boolean>;
  }>({
    base: {},
  });

  const valid = () => {
    let hasErrors = false;

    // Validate all base fields
    for (const key of Object.keys(paymentprofile) as (keyof Paymentprofile)[]) {
      if (typeof paymentprofile[key] !== "object" && validateBaseField(key)) {
        hasErrors = true;
      }
    }

    setSubmitted(true);
    return !hasErrors;
  }

  const getPaymentprofile = () => {
    return paymentprofile;
  }

  // Expose the valid function to the parent, and a method to get the whole paymentprofile object
  useImperativeHandle(ref, () => ({
    valid,
    getPaymentprofile,
  }));

  const isValidFormatAvaliableForChange = (value: string): boolean => {
    if (value == ""){
      return true
    }
    const regex = /^(\d+)(,\d+)*$/;
    return regex.test(value);
  };

  const validateBaseField = (field: keyof Paymentprofile): string => {
    if (field === "name") {
      if (!paymentprofile.name?.trim()) return "Navn tom";
    } else if (field === "available_options_for_change") {
      if (!isValidFormatAvaliableForChange(paymentprofile.available_options_for_change)) 
        return "Kriterier for endring av betlaingsmåte må skrives på riktig format, eksempel: (1,4,3)";
    }
    return ""; // No error
  };

  const handleBaseChange = (field: keyof Paymentprofile, value: any) => {
    setPaymentprofile((prev) => ({ ...prev, [field]: value }));

    // setTouched((prev) => ({
    //   ...prev,
    //   base: { ...prev.base, [field]: true },
    // }));
  };

  const handleBaseBlur = (field: keyof Paymentprofile) => {
    setTouched((prev) => ({
      ...prev,
      base: { ...prev.base, [field]: true },
    }));
  };

  return (
    <form>
      <Grid container spacing={2}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        {error && <Alert sx={{ width: '100%' }} severity="error">{error}</Alert>}

        <Grid item xs={12}>
          <TextField
            label="Navn"
            required
            fullWidth
            value={paymentprofile.name}
            onChange={(e) => handleBaseChange("name", e.target.value)}
            variant="outlined"
            error={validateBaseField("name") !== "" && (submitted || touched.base.name)}
            helperText={(submitted || touched.base.name) ? validateBaseField("name") : ""}
            onBlur={() => handleBaseBlur("name")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={!!paymentprofile.post_ordering}
                onChange={(e) => handleBaseChange("post_ordering", e.target.checked)}
              />
            }
            label="Post bestilling"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={!!paymentprofile.salary_deduction}
                onChange={(e) => handleBaseChange("salary_deduction", e.target.checked)}
              />
            }
            label="Lønnstrekk"
          />
        </Grid>
        {/* <Grid item xs={12} sm={6}>
          <TextField
            label="Post ordering"
            fullWidth
            // autoComplete={autocomplete ? "given-name" : ""}
            value={paymentprofile.post_ordering}
            onChange={(e) => handleBaseChange("post_ordering", e.target.value)}
            variant="outlined"
            error={validateBaseField("post_ordering") !== "" && (submitted || touched.base.post_ordering)}
            helperText={(submitted || touched.base.post_ordering) ? validateBaseField("post_ordering") : ""}
            onBlur={() => handleBaseBlur("post_ordering")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Lønnstrekk"
            fullWidth
            // autoComplete={autocomplete ? "given-name" : ""}
            value={paymentprofile.salary_deduction}
            onChange={(e) => handleBaseChange("salary_deduction", e.target.value)}
            variant="outlined"
            error={validateBaseField("salary_deduction") !== "" && (submitted || touched.base.salary_deduction)}
            helperText={(submitted || touched.base.salary_deduction) ? validateBaseField("salary_deduction") : ""}
            onBlur={() => handleBaseBlur("salary_deduction")}
          />
        </Grid> */}
        <Grid item xs={12} sm={6}>
          <TextField
            label="Ekstern ID"
            fullWidth
            // autoComplete={autocomplete ? "given-name" : ""}
            value={paymentprofile.external_id}
            onChange={(e) => handleBaseChange("external_id", e.target.value)}
            variant="outlined"
            error={validateBaseField("external_id") !== "" && (submitted || touched.base.external_id)}
            helperText={(submitted || touched.base.external_id) ? validateBaseField("external_id") : ""}
            onBlur={() => handleBaseBlur("external_id")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Kriterier for endring av betalingsmåte"
            fullWidth
            autoComplete={autocomplete ? "family-name" : ""}
            value={paymentprofile.available_options_for_change}
            onChange={(e) => handleBaseChange("available_options_for_change", e.target.value)}
            variant="outlined"
            error={validateBaseField("available_options_for_change") !== "" && (submitted || touched.base.available_options_for_change)}
            helperText={(submitted || touched.base.available_options_for_change) ? validateBaseField("available_options_for_change") : ""}
            onBlur={() => handleBaseBlur("available_options_for_change")}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SingleSelectSearch
            label="Forskuddsbetaling løsning"
            inputs={PrepaymentSolutionList || []}
            selectField="name"
            selected={paymentprofile.order_prepayment_solution_id}
            onChange={(id) => {
              const selectedSolution = PrepaymentSolutionList.find((ps) => ps.id === id);
              if (selectedSolution) {
                setPaymentprofile((prev) => ({
                  ...prev,
                  order_prepayment_solution_id: selectedSolution.id,
                }));
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <SingleSelectSearch
            label="Erp løsning"
            inputs={ErpSolutionList || []}
            selectField="name"
            selected={paymentprofile.order_erp_solution_id}
            onChange={(id) => {
              const selectedSolution = ErpSolutionList.find((es) => es.id === id);
              if (selectedSolution) {
                setPaymentprofile((prev) => ({
                  ...prev,
                  order_erp_solution_id: selectedSolution.id,
                }));
              }
            }}
          />
        </Grid>
      </Grid>
    </form>
  )
});